import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

//BIKENTA
import BiomasCO2Chart from './BiomasCO2Chart';
import TotalTons from './TotalTons';

//GEOCOMPONENTS
import SelectInput from '@geomatico/geocomponents/SelectInput';

//UTILS
import {ADDITIONALITY, CALCULATIONS, CO2_LABELS, CO2_TYPES} from '../../../../config';
import {useTranslation} from 'react-i18next';
import {getLabelById} from '../../../../utils/getLabelById';

const AbsorptionPlanSeccion = ({minChartYear, maxChartYear, firstCalculation, finalCalculation, isMonitoring}) => {

  const {t} = useTranslation();

  const [ selectedCo2Id, setSelectedCo2Id ] = useState(CO2_TYPES.TOTAL);

  const [showFirstCalculation, setShowFirstCalculation] = useState(false);
  const [showAdditionality, setShowAdditionality] = useState(false);

  const format = useCallback((data) => data && data.prediction && data.prediction
    .flatMap(({ year, ...co2Data }) =>
      Object.entries(co2Data).map(([co2, value]) => ({
        year: year,
        value: value.toFixed(),
        co2: getLabelById(co2, CO2_LABELS),
        type: getLabelById(data.type, CALCULATIONS)
      })))
    .filter(d => d.co2 !== CO2_TYPES.INTERVENTIONS)
    .filter(d => d.year >= minChartYear && d.year <= maxChartYear), [minChartYear, maxChartYear]);

  const getLastTonsByCo2Type = (data, co2Type) => {
    if (!Array.isArray(data) || data.length === 0) return undefined;
    const filtered = data.filter(item => item.co2 === getLabelById(co2Type, CO2_LABELS));
    if (filtered.length === 0) return null;
    
    const latest = filtered.reduce((max, current) => {
      return Number(current.year) > Number(max.year) ? current : max;
    });

    return latest.value;
  };

  const formattedFirstCalculation = firstCalculation && format(firstCalculation);
  const formattedFinalCalculation = finalCalculation && format(finalCalculation);

  const validAdditionalityCo2Labels = isMonitoring
    ? CO2_LABELS.filter(l => l.id !== CO2_TYPES.TOTAL)
    : CO2_LABELS.filter(l => l.id !== CO2_TYPES.TOTAL && l.id !== CO2_TYPES.INTERVENTIONS);

  const validSelectOptions = isMonitoring
    ? CO2_LABELS
    : CO2_LABELS.filter(l => l.id !== CO2_TYPES.INTERVENTIONS);

  const handleSelectedCo2Id = (id) => {
    if (id === CO2_TYPES.INTERVENTIONS) {
      setShowFirstCalculation(false);
      setShowAdditionality(false);
    }
    setSelectedCo2Id(id);
  };

  return <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4}}>
    {
      (firstCalculation?.prediction || finalCalculation?.prediction) &&
        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
          <BiomasCO2Chart
            minChartYear={minChartYear}
            maxChartYear={maxChartYear}
            firstCalculation={formattedFirstCalculation || []}
            finalCalculation={formattedFinalCalculation || []}
            selectedCo2Id={selectedCo2Id}
            isMonitoring={isMonitoring}
            showFirstCalculation={isMonitoring && showFirstCalculation}
            showAdditionality={showAdditionality}
          />
          
          <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', mt: 1.5}}>
            <SelectInput options={validSelectOptions} selectedOptionId={selectedCo2Id} onOptionChange={handleSelectedCo2Id}/>
            {
              isMonitoring && <FormGroup sx={{display: 'flex', flexDirection: 'column'}}>
                <FormControlLabel disabled={selectedCo2Id === CO2_TYPES.INTERVENTIONS}
                  control={<Checkbox size='small' onChange={() => setShowFirstCalculation(!showFirstCalculation)} value={showFirstCalculation}/>}
                  label={t('Mostrar Línea Base')}
                />
                <FormControlLabel disabled={!formattedFinalCalculation || selectedCo2Id === CO2_TYPES.INTERVENTIONS}
                  control={<Checkbox size='small' onChange={() => setShowAdditionality(!showAdditionality)} value={showAdditionality}/>}
                  label={t('Mostrar adicionalidad')}
                />
              </FormGroup>
            }
            <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mt: isMonitoring ? 0 : 3, gap: 1, width: '350px'}}>
              {
                firstCalculation?.prediction && (
                  !isMonitoring || showFirstCalculation
                    ? <TotalTons label={formattedFirstCalculation[0]?.type} number={getLastTonsByCo2Type(formattedFirstCalculation, selectedCo2Id)}/>
                    : null
                )
              }
              {
                finalCalculation?.prediction &&
                  <TotalTons label={formattedFinalCalculation[0]?.type} number={getLastTonsByCo2Type(formattedFinalCalculation, selectedCo2Id)}/>
              }
              {
                (!isMonitoring || showAdditionality) && firstCalculation?.prediction && finalCalculation?.prediction &&
                <TotalTons 
                  label={getLabelById(ADDITIONALITY, CALCULATIONS) || ''}
                  number={getLastTonsByCo2Type(formattedFinalCalculation, selectedCo2Id)-getLastTonsByCo2Type(formattedFirstCalculation, selectedCo2Id)}/>
              }
            </Box>
          </Box>
        </Box>
    }
    {
      (!isMonitoring || showAdditionality) && <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '80%'}}>
        {
          finalCalculation?.prediction && validAdditionalityCo2Labels
            .map(({id}) =>
              <TotalTons key={id} size='small' 
                label={getLabelById(id, CALCULATIONS) || getLabelById(id, CO2_LABELS) || ''}
                number={
                  id === CO2_TYPES.INTERVENTIONS
                    ? getLastTonsByCo2Type(formattedFinalCalculation, selectedCo2Id)
                    : getLastTonsByCo2Type(formattedFinalCalculation, id) - getLastTonsByCo2Type(formattedFirstCalculation, id)
                }
              />)
        }
      </Box>}
  </Box>;
};

AbsorptionPlanSeccion.propTypes = {
  minChartYear: PropTypes.number.isRequired,
  maxChartYear: PropTypes.number.isRequired,
  isMonitoring: PropTypes.bool.isRequired,
  firstCalculation: PropTypes.object,
  finalCalculation: PropTypes.object
};

export default AbsorptionPlanSeccion;